import { type BusinessHours, type GarageModel } from '@parkingbase/ecommerce-api-dev';
import {
  parseISO,
  getDay,
  addDays,
  isBefore,
  isAfter,
  isEqual,
  differenceInMinutes,
  setHours,
  setMinutes,
  setSeconds,
  setMilliseconds,
  startOfDay,
} from 'date-fns';
/**
 * Minimal garage shape the operating-hours math needs. Kept structural (not tied to
 * a single API model) so both the touchless `GarageModel` and the reserve flow's
 * `GarageResult` — which carries `allowOvernightReservations` nested under
 * `transientPricingConfig` — can be adapted to it. (PB-3060)
 */
export interface OvernightGarageInput {
  hours?: ReadonlyArray<{ open?: string | null; close?: string | null }> | null;
  allowOvernightReservations?: boolean | null;
}
interface OvernightDetails {
  isParkingOvernight: boolean;
  isGarageClosedDuringBooking: boolean;
}

interface Interval {
  start: Date;
  end: Date;
}

function intervalContains(interval: Interval, date: Date): boolean {
  return isAfter(date, interval.start) && isBefore(date, interval.end);
}

function getDayIndex(dayOfWeek: number): number {
  if (dayOfWeek > 6) return 0;
  if (dayOfWeek < 0) return 6;
  return dayOfWeek;
}

/**
 * Parse a time string like "08:00:00" or "22:30:00" and apply it to a given date.
 */
function applyTimeToDate(timeStr: string, date: Date): Date {
  const parts = timeStr.split(':');
  const h = parseInt(parts[0], 10);
  const m = parseInt(parts[1], 10);
  const s = parts[2] ? parseInt(parts[2], 10) : 0;
  let result = startOfDay(date);
  result = setHours(result, h);
  result = setMinutes(result, m);
  result = setSeconds(result, s);
  result = setMilliseconds(result, 0);
  return result;
}

function isWithinRangeInternal(rangeStart: Date, rangeEnd: Date, start: Date, end: Date): boolean {
  return (
    (isAfter(start, rangeStart) || isEqual(start, rangeStart)) &&
    (isBefore(end, rangeEnd) || isEqual(end, rangeEnd))
  );
}

/**
 * Detect whether a parking session spanning [start, end] crosses overnight
 * garage closure windows, and whether the start/end falls within a closure.
 *
 * Ported from vm-reservation-website/src/pages/garage-list/ReservationHelper.ts
 */
export function getOvernightDetails(
  garage: OvernightGarageInput,
  startISO: string,
  endISO: string
): OvernightDetails {
  const hours = garage.hours;
  if (!hours || hours.length < 7) {
    return { isParkingOvernight: false, isGarageClosedDuringBooking: false };
  }

  const startDate = parseISO(startISO);
  const endDate = parseISO(endISO);

  let startDayIndex = getDay(startDate);
  let endDayIndex = getDay(endDate);

  let tempStart = new Date(startDate);
  let tempEnd = new Date(endDate);

  const getHours = (dayIdx: number): { open?: string | null; close?: string | null } =>
    hours[dayIdx] ?? {};

  // Find garage open/close on start day
  let garageOpenAtStart = applyTimeToDate(getHours(startDayIndex).open ?? '00:00:00', startDate);
  let garageCloseAtStart = applyTimeToDate(getHours(startDayIndex).close ?? '23:59:59', startDate);

  // Handle late-night close (close < open means next-day close)
  if (isBefore(garageCloseAtStart, garageOpenAtStart)) {
    garageCloseAtStart = addDays(garageCloseAtStart, 1);
  }

  // Find garage open/close on end day
  const garageOpenAtEnd = applyTimeToDate(getHours(endDayIndex).open ?? '00:00:00', endDate);
  let garageCloseAtEnd = applyTimeToDate(getHours(endDayIndex).close ?? '23:59:59', endDate);

  if (isBefore(garageCloseAtEnd, garageOpenAtEnd)) {
    garageCloseAtEnd = addDays(garageCloseAtEnd, 1);
    tempEnd = addDays(tempEnd, 1);
    endDayIndex = getDayIndex(endDayIndex + 1);
  }

  // If start is before garage open, look at previous day
  if (isBefore(startDate, garageOpenAtStart)) {
    startDayIndex = getDayIndex(startDayIndex - 1);
    tempStart = addDays(tempStart, -1);
    garageOpenAtStart = applyTimeToDate(getHours(startDayIndex).open ?? '00:00:00', tempStart);
    garageCloseAtStart = applyTimeToDate(getHours(startDayIndex).close ?? '23:59:59', tempStart);
  }

  if (isBefore(garageCloseAtStart, garageOpenAtStart)) {
    garageCloseAtStart = addDays(garageCloseAtStart, 1);
  }

  // If start and end fall within one open/close window, no overnight
  if (isWithinRangeInternal(garageOpenAtStart, garageCloseAtStart, startDate, endDate)) {
    return { isParkingOvernight: false, isGarageClosedDuringBooking: false };
  }

  // If garage close at end is before end date, extend to next day
  if (isBefore(garageCloseAtEnd, endDate)) {
    endDayIndex = getDayIndex(endDayIndex + 1);
    tempEnd = addDays(tempEnd, 1);
    garageCloseAtEnd = applyTimeToDate(getHours(endDayIndex).close ?? '23:59:59', tempEnd);
  }

  // Build intervals from open→close for each day between start and end
  const intervals: Interval[] = [];
  while (isBefore(garageOpenAtStart, garageCloseAtEnd)) {
    intervals.push({ start: garageOpenAtStart, end: garageCloseAtStart });
    tempStart = addDays(tempStart, 1);
    startDayIndex = getDayIndex(startDayIndex + 1);
    garageOpenAtStart = applyTimeToDate(getHours(startDayIndex).open ?? '00:00:00', tempStart);
    garageCloseAtStart = applyTimeToDate(getHours(startDayIndex).close ?? '23:59:59', tempStart);
    if (isBefore(garageCloseAtStart, garageOpenAtStart)) {
      garageCloseAtStart = addDays(garageCloseAtStart, 1);
    }
  }

  let isOvernight = false;
  let isGarageClosed = false;

  for (let i = 0; i < intervals.length; i++) {
    const current = intervals[i];
    const next: Interval | undefined = intervals[i + 1];

    // Gap > 30 min between consecutive close/open = overnight
    if (next && differenceInMinutes(next.start, current.end) > 30) {
      isOvernight = true;
    }

    if (!next) continue;

    // Skip overlapping intervals (bad data)
    if (isBefore(next.start, current.end)) continue;

    // Closure interval between current close and next open
    const closed: Interval = { start: current.end, end: next.start };

    // If start or end falls in the closure AND it's overnight, flag it
    if ((intervalContains(closed, startDate) || intervalContains(closed, endDate)) && isOvernight) {
      isGarageClosed = true;
    }
  }

  return {
    isParkingOvernight: isOvernight,
    isGarageClosedDuringBooking: isGarageClosed,
  };
}

/**
 * Check whether a booking [startDate, endDate] is valid given the garage's
 * business hours and overnight parking rules.
 *
 * Returns `true` if:
 * - The garage is NOT closed during the booking window, AND
 * - The booking is NOT overnight OR the garage allows overnight reservations
 *
 * Ported from vm-reservation-website/src/common/constants.ts
 */
export function isGarageWithinRange(
  garage: OvernightGarageInput,
  startDate: Date,
  endDate: Date
): boolean {
  const start = setMilliseconds(setSeconds(new Date(startDate), 0), 0);
  const end = setMilliseconds(setSeconds(new Date(endDate), 0), 0);

  const details = getOvernightDetails(garage, start.toISOString(), end.toISOString());

  return (
    !details.isGarageClosedDuringBooking &&
    (!details.isParkingOvernight || !!garage.allowOvernightReservations)
  );
}
